import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Button, TableBody, TableCell, TableHead, TableRow, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Box, Grid, Alert, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import EmptyState from '../EmptyState';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import useTableSearch from '../../hooks/useTableSearch';

const APPLIES_TO = [
  { value: 'both', label: 'Invoices and payments' },
  { value: 'invoice', label: 'Invoices only' },
  { value: 'payment', label: 'Payments only' }
];

const appliesToLabel = (v) => (APPLIES_TO.find((a) => a.value === v) || {}).label || v;

// Mirrors backend/utils/chartOfAccounts.js AGENCY_INCOME_CODES — income that belongs to the
// agency rather than the landlord whose property it arose on. Keep the two in step: this
// screen is where someone decides whether a new charge type earns the agency or the landlord,
// and a stale copy here labels that decision wrongly at the moment it is made.
const AGENCY_INCOME_CODES = ['4100', '4500', '4600'];

const BLANK = { name: '', code: '', description: '', appliesTo: 'both', sortOrder: 100, incomeAccountCode: '' };

// What an invoice or a payment can be FOR.
//
// These were hardcoded in five dropdowns and two database ENUMs, so adding "Service Charge"
// meant a code change and an ALTER TABLE. They are data now — with two exceptions the screen
// is careful to explain rather than merely refuse.
const ChargeTypeManagement = () => {
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [incomeAccounts, setIncomeAccounts] = useState([]);

  const {
    query, setQuery, reset, rows: visible, resultCount, totalCount
  } = useTableSearch(types, { searchFields: ['name', 'code', 'description'] });

  useEffect(() => { fetchTypes(); fetchIncomeAccounts(); }, []);

  // Only income accounts: an invoice credits income, and the server refuses anything else.
  const fetchIncomeAccounts = async () => {
    try {
      const res = await axios.get('/accounting/chart-of-accounts');
      const all = Array.isArray(res.data) ? res.data : (res.data.data || []);
      setIncomeAccounts(all.filter((a) => a.type === 'income' && a.isActive !== false));
    } catch (e) {
      setIncomeAccounts([]);
    }
  };

  const fetchTypes = async () => {
    try {
      const res = await axios.get('/charge-types');
      setTypes(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load charge types');
    }
  };

  const handleOpen = (type = null) => {
    if (type) {
      setEditing(type._id);
      setForm({
        name: type.name || '',
        code: type.code || '',
        description: type.description || '',
        appliesTo: type.appliesTo || 'both',
        sortOrder: type.sortOrder ?? 100,
        incomeAccountCode: type.incomeAccountCode || ''
      });
    } else {
      setEditing(null);
      setForm(BLANK);
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Give the type a name'); return; }
    if (!form.incomeAccountCode) {
      toast.error('Choose the income account this type credits');
      return;
    }
    try {
      if (editing) {
        // The code is never sent on an edit — the server refuses to change it, and sending
        // it unchanged just invites a needless comparison.
        const { code, ...editable } = form;
        await axios.put(`/charge-types/${editing}`, editable);
        toast.success('Charge type updated');
      } else {
        await axios.post('/charge-types', form);
        toast.success('Charge type added');
      }
      setOpen(false);
      fetchTypes();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save this charge type');
    }
  };

  // Asks the server what is using it BEFORE offering to delete, so the refusal explains
  // itself in terms of records rather than arriving as an error after the click.
  const handleDelete = async (type) => {
    try {
      const { data: usage } = await axios.get(`/charge-types/${type._id}/usage`);
      if (!usage.deletable) {
        const parts = [];
        if (usage.invoices) parts.push(`${usage.invoices} invoice(s)`);
        if (usage.payments) parts.push(`${usage.payments} payment(s)`);
        toast.info(parts.length
          ? `'${type.name}' is used by ${parts.join(' and ')}. Deactivate it instead — it stops being offered and the existing records keep their history.`
          : `'${type.name}' cannot be deleted.`);
        return;
      }
      if (!window.confirm(`Delete '${type.name}'? Nothing uses it, so this removes it outright.`)) return;
      await axios.delete(`/charge-types/${type._id}`);
      toast.success('Charge type deleted');
      fetchTypes();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not delete this charge type');
    }
  };

  const toggleActive = async (type) => {
    try {
      await axios.put(`/charge-types/${type._id}`, { isActive: !type.isActive });
      toast.success(type.isActive
        ? `'${type.name}' will no longer be offered on new records`
        : `'${type.name}' is available again`);
      fetchTypes();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not change this charge type');
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Charge Types</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Charge Type
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        What an invoice or a payment is <strong>for</strong>. These fill the Type dropdowns on
        the invoice and payment forms. Deactivating one stops it being offered on new records
        without touching the ones already raised under it.
      </Alert>

      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Name, code or description"
        onReset={reset}
        resultCount={resultCount}
        totalCount={totalCount}
      />

      <ScrollTable>
        <TableHead>
          <TableRow>
            <TableCell className="pin-left">Name</TableCell>
            <TableCell>Code</TableCell>
            <TableCell>Applies to</TableCell>
            <TableCell>Credits</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Status</TableCell>
            <TableCell className="pin-right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.map((type) => (
            <TableRow key={type._id} hover>
              <TableCell className="pin-left">
                <Box display="flex" alignItems="center" gap={0.5}>
                  {type.name}
                  {/* Said with a tooltip rather than by hiding the row: an admin looking for
                      "Rent" needs to find it and understand why it behaves differently. */}
                  {type.isSystem && (
                    <Tooltip title={
                      type.code === 'rent'
                        ? 'Rent invoicing depends on this type — it carries the rental period and settles first. The name can be changed; the rest cannot.'
                        : 'Late payment penalties depend on this type — it is agency income and settles last. The name can be changed; the rest cannot.'
                    }>
                      <LockIcon fontSize="inherit" color="disabled" />
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">{type.code}</Typography>
              </TableCell>
              <TableCell>{appliesToLabel(type.appliesTo)}</TableCell>
              <TableCell>
                <Typography variant="body2">{type.incomeAccountCode}</Typography>
                {AGENCY_INCOME_CODES.includes(type.incomeAccountCode) && (
                  /* Worth saying on the row: agency income is deliberately left out of every
                     landlord-facing figure, so this choice decides whose money it is. */
                  <Typography variant="caption" color="text.secondary">agency income</Typography>
                )}
              </TableCell>
              <TableCell>{type.description || '—'}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={type.isActive ? 'Active' : 'Inactive'}
                  color={type.isActive ? 'success' : 'default'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell className="pin-right">
                <Box display="flex" gap={1}>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => handleOpen(type)}>
                    Edit
                  </Button>
                  {!type.isSystem && (
                    <Button size="small" onClick={() => toggleActive(type)}>
                      {type.isActive ? 'Deactivate' : 'Reactivate'}
                    </Button>
                  )}
                  {!type.isSystem && (
                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(type)}>
                      Delete
                    </Button>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {visible.length === 0 && (
            <TableRow>
              <TableCell colSpan={7}>
                <EmptyState
                  title={query ? 'No charge type matches' : 'No charge types yet'}
                  description={query ? 'Try a different name or code.' : 'Add one to offer it on the invoice and payment forms.'}
                />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </ScrollTable>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Charge Type' : 'Add Charge Type'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth required label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                helperText="What staff see in the Type dropdown"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Code"
                value={editing ? form.code : (form.code || '')}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                disabled={Boolean(editing)}
                helperText={editing
                  // Explained rather than just greyed out — otherwise it reads as an
                  // oversight, and someone files a bug about it.
                  ? 'Fixed once created: every invoice and payment raised under this type stores this value.'
                  : 'Leave blank to build one from the name. Lowercase letters, numbers and underscores.'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth select required label="Credits which income account"
                value={form.incomeAccountCode}
                onChange={(e) => setForm({ ...form, incomeAccountCode: e.target.value })}
                helperText={
                  AGENCY_INCOME_CODES.includes(form.incomeAccountCode)
                    ? "Agency income — kept out of the landlord's statement and payout."
                    : "Where invoices of this type post. Landlord income unless an agency account is chosen."
                }
              >
                {incomeAccounts.map((a) => (
                  <MenuItem key={a.code} value={a.code}>
                    {a.code} — {a.name}{AGENCY_INCOME_CODES.includes(a.code) ? ' (agency)' : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth select label="Applies to"
                value={form.appliesTo}
                onChange={(e) => setForm({ ...form, appliesTo: e.target.value })}
              >
                {APPLIES_TO.map((a) => (
                  <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth type="number" label="Sort Order"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                helperText="Lower shows first"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={2} label="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!form.name.trim()}>
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ChargeTypeManagement;
